import { useState } from "react";
import { Printer, User as UserIcon } from "lucide-react";

/**
 * One access card, rendered two ways — on screen and on paper — from one
 * definition. The member prints theirs from /my-events/:regId and the
 * secretariat reprints the same card from the event's registrations tab, so
 * the two must not be allowed to drift apart.
 *
 * `card` is the API's access-card payload (GET /my/events/{id}/access-card, or
 * the admin twin). `event` is optional: the member page already holds the
 * event, the admin modal reads it off the card itself.
 */
export type AccessCardData = {
  registration_no?: string | null;
  barcode_data?: string | null;
  attendee_name?: string | null;
  name?: string | null;
  attendee_email?: string | null;
  photo_url?: string | null;
  membership_no?: string | null;
  event_title?: string | null;
  event_location?: string | null;
  event_start_date?: string | null;
  event_end_date?: string | null;
};

type EventLike = { title?: string | null; location?: string | null; start_date?: string | null; end_date?: string | null };

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" }) : "";

// Escapes into the print window's HTML. Names and venues are free text typed by
// members and admins; without this, an apostrophe or a stray angle bracket in
// one of them silently breaks the card it is printed on.
const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * Scanning this QR opens /scan/{barcode} on whichever host the card was
 * printed from, where an admin marks attendance and issues food and materials.
 */
export function scanUrl(barcode: string) {
  return `${window.location.origin}/scan/${barcode}`;
}

export function qrSrc(barcode: string, size = 220) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(scanUrl(barcode))}`;
}

function resolve(card: AccessCardData, event?: EventLike) {
  return {
    name:      card.attendee_name ?? card.name ?? "",
    regNo:     card.registration_no ?? "",
    barcode:   card.barcode_data ?? "",
    photo:     card.photo_url ?? null,
    memberNo:  card.membership_no ?? null,
    title:     event?.title ?? card.event_title ?? "Event",
    location:  event?.location ?? card.event_location ?? "",
    startDate: event?.start_date ?? card.event_start_date ?? "",
  };
}

/**
 * Opens the card in a print window. The holder's photograph sits beside the
 * QR: the QR proves the card was issued, the face proves who is carrying it,
 * and a gate that can only check the first is a gate anyone can walk through
 * with a borrowed card.
 */
export function printAccessCard(card: AccessCardData, event?: EventLike) {
  const c = resolve(card, event);
  if (!c.barcode) return;

  const w = window.open("", "_blank", "width=640,height=900");
  if (!w) return;

  const photoBlock = c.photo
    ? `<img class="photo" src="${esc(c.photo)}" alt="Photograph" onerror="this.parentNode.removeChild(this)" />`
    : `<div class="photo placeholder">No photo<br>on file</div>`;

  w.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Access Card${c.regNo ? ` — ${esc(c.regNo)}` : ""}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;font-family:'Segoe UI',Arial,sans-serif}
    body{background:#f4f4f4;display:flex;justify-content:center;padding:40px}
    .card{width:360px;background:#fff;border:2px solid #111;border-radius:20px;overflow:hidden;box-shadow:0 10px 40px rgba(0,0,0,.15)}
    .header{background:#111;padding:22px 26px 18px}
    .header .org{font-size:10px;font-weight:900;letter-spacing:.22em;text-transform:uppercase;color:#777;margin-bottom:5px}
    .header .event{font-size:15px;font-weight:900;color:#fff;line-height:1.3}
    .holder{display:flex;align-items:center;gap:16px;padding:22px 26px 18px;border-bottom:1px solid #f0f0f0}
    .photo{width:88px;height:88px;border-radius:14px;object-fit:cover;border:2px solid #111;flex-shrink:0;background:#f4f4f4}
    .photo.placeholder{display:flex;align-items:center;justify-content:center;text-align:center;font-size:9px;font-weight:900;letter-spacing:.1em;text-transform:uppercase;color:#bbb;border-style:dashed;border-color:#ddd}
    .who{min-width:0}
    .name{font-size:17px;font-weight:900;color:#111;line-height:1.25}
    .reg{font-size:9px;font-weight:900;letter-spacing:.22em;text-transform:uppercase;color:#aaa;margin-top:5px}
    .mno{font-size:10px;font-weight:900;color:#4f46e5;margin-top:3px}
    .body{padding:22px 26px;display:flex;flex-direction:column;align-items:center;gap:14px}
    .qr{width:200px;height:200px;border-radius:12px;border:1px solid #eee}
    .meta{font-size:11px;color:#666;text-align:center;line-height:1.6}
    .footer{background:#f9f9f9;border-top:1px solid #eee;padding:14px 26px;text-align:center}
    .footer span{font-size:9px;font-weight:900;letter-spacing:.2em;text-transform:uppercase;color:#bbb}
    @media print{body{background:#fff;padding:0}.card{box-shadow:none}}
  </style>
</head>
<body>
  <div class="card">
    <div class="header">
      <div class="org">NIQS Nigeria &middot; Event Access Card</div>
      <div class="event">${esc(c.title)}</div>
    </div>
    <div class="holder">
      ${photoBlock}
      <div class="who">
        <div class="name">${esc(c.name)}</div>
        <div class="reg">${esc(c.regNo)}</div>
        ${c.memberNo ? `<div class="mno">${esc(c.memberNo)}</div>` : ""}
      </div>
    </div>
    <div class="body">
      <img class="qr" src="${esc(qrSrc(c.barcode, 260))}" alt="QR Code" />
      <div class="meta">${c.startDate ? `&#128197; ${esc(fmtDate(c.startDate))}` : ""}${c.location ? `<br>&#128205; ${esc(c.location)}` : ""}</div>
    </div>
    <div class="footer"><span>Present this card at the event entrance for check-in</span></div>
  </div>
  <script>window.onload=function(){window.print()}</script>
</body>
</html>`);
  w.document.close();
}

/** The same card on screen, above a Print button. */
export function AccessCardView({
  card, event, onPrint,
}: {
  card: AccessCardData;
  event?: EventLike;
  /** Hidden when false — the member page hides it until the fee is settled. */
  onPrint?: boolean;
}) {
  const c = resolve(card, event);
  const [photoBroken, setPhotoBroken] = useState(false);
  const showPhoto = !!c.photo && !photoBroken;

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="bg-gray-50 rounded-2xl p-6 flex flex-col items-center gap-4 w-full max-w-xs">

        {/* Holder — photograph first, because that is what the gate checks. */}
        <div className="flex items-center gap-4 w-full">
          {showPhoto ? (
            <img
              src={c.photo!}
              alt={c.name}
              onError={() => setPhotoBroken(true)}
              className="w-20 h-20 rounded-2xl object-cover border-2 border-gray-900 shrink-0"
            />
          ) : (
            <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-300 shrink-0">
              <UserIcon className="w-6 h-6" />
              <span className="text-[8px] font-black uppercase tracking-widest mt-1">No photo</span>
            </div>
          )}
          <div className="min-w-0">
            <div className="font-black text-gray-900 text-sm leading-tight">{c.name}</div>
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">{c.regNo}</div>
            {c.memberNo && <div className="text-[10px] font-black text-indigo-600 mt-0.5">{c.memberNo}</div>}
          </div>
        </div>

        {c.barcode && (
          <img
            src={qrSrc(c.barcode, 180)}
            alt="Access QR Code"
            className="w-44 h-44 rounded-xl"
          />
        )}
      </div>

      <p className="text-xs text-gray-400 font-medium text-center">
        Present this QR code at the event entrance for check-in.
      </p>

      {onPrint !== false && c.barcode && (
        <button
          onClick={() => printAccessCard(card, event)}
          className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white font-black text-xs py-3 rounded-2xl hover:bg-indigo-700 transition-colors"
        >
          <Printer className="w-3.5 h-3.5" /> Print Access Card
        </button>
      )}
    </div>
  );
}
